import React from 'react';
import { AxiosResponse } from 'axios';
import { useAuth } from './useAuth';
import { FilterOptionModel, JsonPatch } from 'utils/models';
import { prepareQueryParams } from 'utils';
import { FILTERED_ENTITIES } from './apiEndPoints';

/**
 * Configuration options for creating a CRUD hook
 */
export interface CRUDHookConfig<TDto, TList = TDto[], TCustom = {}> {
  /** Name of the entity (e.g., 'Size', 'Color', 'Carrier') */
  entityName: string;
  /** API endpoint for the entity */
  endpoint: string;
  /** Optional custom operations to add to the hook */
  customOperations?: (auth: any) => TCustom;
}

/**
 * Standard CRUD operations interface
 */
export interface CRUDOperations<TDto, TList = TDto[]> {
  /** Get all data */
  getData: () => Promise<TList>;
  /** Get data by ID */
  getDataById: (id: number | string) => Promise<TDto>;
  /** Get data with filter options */
  getDataByFilteredEntities: (
    filteredEntities: FilterOptionModel
  ) => Promise<TList>;
  /** Add new data */
  addData: (param: TDto) => Promise<TDto>;
  /** Update data using PATCH with JsonPatch array */
  updateData: (param: JsonPatch[], id: number) => Promise<AxiosResponse>;
  /** Update data using PUT */
  updateDataWithPut: (param: TDto) => Promise<AxiosResponse>;
  /** Delete data by ID */
  deleteData: (id: number) => Promise<boolean>;
}

/**
 * Generic CRUD Hook Factory
 *
 * Creates a complete CRUD hook with Context, Manager, and Consumer hooks
 *
 * @example
 * ```typescript
 * // Basic usage
 * const { Context: SizeContext, useManager: useSizeManager, useConsumer: useSize } =
 *   createCRUDHook<SizeDto>({
 *     entityName: 'Size',
 *     endpoint: TLKP_SIZE
 *   });
 *
 * // With custom operations (typed)
 * interface PartCustomOperations {
 *   checkPartNumberExist: (id: string) => Promise<boolean>;
 * }
 *
 * const { Context: PartContext, useManager: usePartManager, useConsumer: usePart } =
 *   createCRUDHook<PartsDto, PartList, PartCustomOperations>({
 *     entityName: 'Part',
 *     endpoint: TBL_PARTS,
 *     customOperations: (auth) => ({
 *       checkPartNumberExist: async (id: string): Promise<boolean> => {
 *         try {
 *           let res = await auth.makeAuthenticatedGet(`${TBL_PARTS}/check?partNumber=${id}`);
 *           return Promise.resolve(res.data);
 *         } catch (error) {
 *           return Promise.reject(error);
 *         }
 *       }
 *     })
 *   });
 * ```
 */
export function createCRUDHook<TDto = any, TList = TDto[], TCustom = {}>(
  config: CRUDHookConfig<TDto, TList, TCustom>
) {
  const { entityName, endpoint, customOperations } = config;

  // Generate interface type
  type ICRUDEntity = CRUDOperations<TDto, TList> & TCustom;

  // Create default context values (type assertion needed as custom operations are added at runtime)
  const defaultContextValue = {
    getData: async () =>
      Promise.reject(new Error(`${entityName} context not initialized`)),
    getDataById: async (id: number | string) =>
      Promise.reject(new Error(`${entityName} context not initialized`)),
    getDataByFilteredEntities: async (filteredEntities: FilterOptionModel) =>
      Promise.reject(new Error(`${entityName} context not initialized`)),
    addData: async (param: TDto) =>
      Promise.reject(new Error(`${entityName} context not initialized`)),
    updateData: async (param: JsonPatch[], id: number) =>
      Promise.reject(new Error(`${entityName} context not initialized`)),
    updateDataWithPut: async (param: TDto) =>
      Promise.reject(new Error(`${entityName} context not initialized`)),
    deleteData: async (id: number) =>
      Promise.reject(new Error(`${entityName} context not initialized`)),
  } as ICRUDEntity;

  // Create Context
  const EntityContext = React.createContext<ICRUDEntity>(defaultContextValue);

  // Create Manager Hook
  function useEntityManager(): ICRUDEntity {
    const auth = useAuth();

    // Standard GET operation
    const getData = async (): Promise<TList> => {
      try {
        const res = await auth.makeAuthenticatedGet(endpoint);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    };

    // GET by ID
    const getDataById = async (id: number | string): Promise<TDto> => {
      try {
        const res = await auth.makeAuthenticatedGet(`${endpoint}/${id}`);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    };

    // GET with filtered entities
    const getDataByFilteredEntities = async (
      filteredEntities: FilterOptionModel
    ): Promise<TList> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${endpoint}${FILTERED_ENTITIES}?${prepareQueryParams(
            filteredEntities
          )}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    };

    // POST operation
    const addData = async (param: TDto): Promise<TDto> => {
      try {
        const res = await auth.makeAuthenticatedPost(endpoint, param);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    };

    // PATCH operation with JsonPatch array
    const updateData = async (
      param: JsonPatch[],
      id: number
    ): Promise<AxiosResponse> => {
      try {
        const res = await auth.makeAuthenticatedPatch(
          `${endpoint}/${id}`,
          param
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    };

    // PUT operation
    const updateDataWithPut = async (param: TDto): Promise<AxiosResponse> => {
      try {
        const res = await auth.makeAuthenticatedPut(endpoint, param);
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    };

    // DELETE operation
    const deleteData = async (id: number): Promise<boolean> => {
      try {
        const res = await auth.makeAuthenticatedDelete(`${endpoint}/${id}`);
        return Promise.resolve(res.data);
      } catch (error) {
        return false;
      }
    };

    // Merge standard operations with custom operations
    const standardOperations = {
      getData,
      getDataById,
      getDataByFilteredEntities,
      addData,
      updateData,
      updateDataWithPut,
      deleteData,
    };

    const custom = customOperations ? customOperations(auth) : ({} as TCustom);

    return {
      ...standardOperations,
      ...custom,
    } as ICRUDEntity;
  }

  // Create Consumer Hook
  function useEntityConsumer(): ICRUDEntity {
    return React.useContext(EntityContext);
  }

  return {
    Context: EntityContext,
    useManager: useEntityManager,
    useConsumer: useEntityConsumer,
  };
}

/**
 * Helper function to create named exports for a CRUD hook
 *
 * @example
 * ```typescript
 * // In useSize.ts
 * export const {
 *   SizeContext,
 *   useSizeManager,
 *   useSize
 * } = createNamedCRUDHook<SizeDto>({
 *   entityName: 'Size',
 *   endpoint: TLKP_SIZE
 * });
 * ```
 */
export function createNamedCRUDHook<TDto = any, TList = TDto[], TCustom = {}>(
  config: CRUDHookConfig<TDto, TList, TCustom>
) {
  const { Context, useManager, useConsumer } = createCRUDHook<TDto, TList, TCustom>(
    config
  );
  const { entityName } = config;

  return {
    [`${entityName}Context`]: Context,
    [`use${entityName}Manager`]: useManager,
    [`use${entityName}`]: useConsumer,
  };
}
